import type { EpisodeCategory } from '@common/enums';
import type {
    BrightPlanLocation,
    BrightPlanOperationType,
    GetOperationTypeQuery,
} from '@common/interfaces/api/brightPlanApi';
import type { BackendBrightPlanScheduleAction } from '@common/validation/patientBrightPlanScheduleAction';
import { ToastType, ValidationErrorCode } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { getUrlQueryParameters } from '@frontend/services/api/queryParameterUtils';
import { fetchJson } from '@frontend/store/fetch';
import { showToast } from '@frontend/utils';

const baseUrl = '/api/brightPlan/';

export async function handleBrightPlanGetSsoUrl(
    patientId?: string,
    episodeCategory?: EpisodeCategory,
    isFlexibleCarePath?: boolean
) {
    const translate = getLanguage();
    const result = await fetchJson(
        `${baseUrl}getSsoUrl`,
        'POST',
        {
            episodeCategory,
            isFlexibleCarePath,
            patientId,
        },
        [ValidationErrorCode.BRIGHTPLAN_USERNAME_NOT_FOUND, ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED]
    );

    if (result.status === 200) {
        window.open(result.json.ssoUrl);
    }

    if (result.errorCode) {
        showToast(
            ToastType.error,
            translate.errorCodes[
                result.errorCode as
                    | ValidationErrorCode.BRIGHTPLAN_USERNAME_NOT_FOUND
                    | ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED
            ]
        );
    }
}

export async function addActionBrightPlan(args: {
    episodeCategoryId: string;
    isFlexibleCarePath: boolean;
    patientId: string;
    scheduleAction: BackendBrightPlanScheduleAction;
}) {
    const { episodeCategoryId, isFlexibleCarePath, patientId, scheduleAction } = args;
    const translate = getLanguage();
    const result = await fetchJson(
        `${baseUrl}${patientId}/${episodeCategoryId}/scheduleAction`,
        'POST',
        { isFlexibleCarePath, scheduleAction },
        [ValidationErrorCode.BRIGHTPLAN_ADD_ACTION_FAILED, ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED]
    );

    if (result.errorCode) {
        showToast(
            ToastType.error,
            translate.errorCodes[
                result.errorCode as
                    | ValidationErrorCode.BRIGHTPLAN_ADD_ACTION_FAILED
                    | ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED
            ]
        );
    }
}

export async function getOperationTypes(query?: GetOperationTypeQuery): Promise<BrightPlanOperationType[]> {
    const url = getUrlQueryParameters<GetOperationTypeQuery>(`${baseUrl}operationTypes`, query);
    const translate = getLanguage();

    try {
        const result = await fetchJson(url, 'GET', undefined, [
            ValidationErrorCode.BRIGHTPLAN_GET_OPERATION_TYPES_FAILED,
            ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED,
        ]);

        if (result.errorCode) {
            showToast(
                ToastType.error,
                translate.errorCodes[
                    result.errorCode as
                        | ValidationErrorCode.BRIGHTPLAN_GET_OPERATION_TYPES_FAILED
                        | ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED
                ]
            );

            return [];
        }

        return result.json.operationTypes;
    } catch (e) {
        console.error(e);

        return [];
    }
}

export async function getLocations(operationTypes: string[]): Promise<BrightPlanLocation[]> {
    const translate = getLanguage();
    const urlBuilder = new UrlBuilder(`${baseUrl}locations`);
    operationTypes.forEach((operationType) => urlBuilder.add('operationTypeIds[]', operationType));

    const result = await fetchJson(urlBuilder.build(), 'GET', undefined, [
        ValidationErrorCode.BRIGHTPLAN_GET_LOCATIONS_FAILED,
        ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED,
    ]);

    if (result.errorCode) {
        showToast(
            ToastType.error,
            translate.errorCodes[
                result.errorCode as
                    | ValidationErrorCode.BRIGHTPLAN_GET_LOCATIONS_FAILED
                    | ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED
            ]
        );

        return [];
    }

    return result.json.locations;
}

export async function getProfessionals(
    operationTypes: string[],
    locations: number[]
): Promise<{ id: string; name: string }[]> {
    const translate = getLanguage();

    const urlBuilder = new UrlBuilder(`${baseUrl}professionals`);
    operationTypes.forEach((operationType) => urlBuilder.add('operationTypeIds[]', operationType));
    locations.forEach((location) => urlBuilder.add('locationIds[]', location));

    const result = await fetchJson(urlBuilder.build(), 'GET', undefined, [
        ValidationErrorCode.BRIGHTPLAN_GET_PROFESSIONALS_FAILED,
        ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED,
    ]);

    if (result.errorCode) {
        showToast(
            ToastType.error,
            translate.errorCodes[
                result.errorCode as
                    | ValidationErrorCode.BRIGHTPLAN_GET_PROFESSIONALS_FAILED
                    | ValidationErrorCode.BRIGHTPLAN_AUTHENTICATION_FAILED
            ]
        );

        return [];
    }

    return result.json.professionals;
}
